// Utility functions for week calculations

/**
 * Get the Monday of the week for a given date
 */
export function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

/**
 * Get the Monday of the current week as ISO date string (YYYY-MM-DD)
 */
export function getCurrentWeekStart(): string {
  const weekStart = getWeekStart();
  const year = weekStart.getFullYear();
  const month = String(weekStart.getMonth() + 1).padStart(2, '0');
  const day = String(weekStart.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the Monday of a specific week as ISO date string
 */
export function getWeekStartString(date: Date = new Date()): string {
  const weekStart = getWeekStart(date);
  const year = weekStart.getFullYear();
  const month = String(weekStart.getMonth() + 1).padStart(2, '0');
  const day = String(weekStart.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all days of the week (Monday to Sunday) for a given week start date
 */
export function getWeekDays(weekStartDate: string): Date[] {
  const start = new Date(weekStartDate);
  const days: Date[] = [];
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    days.push(day);
  }
  
  return days;
}

/**
 * Get day name (Monday, Tuesday, etc.)
 */
export function getDayName(dayIndex: number): string {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days[dayIndex] || '';
}

/**
 * Format date as "Mon, Jan 1"
 */
export function formatDayDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Get the Monday of next week as ISO date string
 */
export function getNextWeekStart(): string {
  const currentWeek = getWeekStart();
  const nextWeek = new Date(currentWeek);
  nextWeek.setDate(currentWeek.getDate() + 7);
  const year = nextWeek.getFullYear();
  const month = String(nextWeek.getMonth() + 1).padStart(2, '0');
  const day = String(nextWeek.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the Monday of the week after next as ISO date string
 */
export function getWeekAfterNextStart(): string {
  const currentWeek = getWeekStart();
  const weekAfter = new Date(currentWeek);
  weekAfter.setDate(currentWeek.getDate() + 14);
  const year = weekAfter.getFullYear();
  const month = String(weekAfter.getMonth() + 1).padStart(2, '0');
  const day = String(weekAfter.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format week range (e.g., "Jan 1 - Jan 7")
 */
export function formatWeekRange(weekStartDate: string): string {
  const start = new Date(weekStartDate);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  
  return `${startStr} - ${endStr}`;
}

